import React, { useState, useEffect, useCallback } from "react";
import { useMutation, graphql, useQuery } from "react-apollo";
import compose from "lodash/flowRight";
import { debounce } from "lodash";
import CURRENT_USER from "../../../GraphQl/Queries/CURRENT_USER";
import ACTIVATE_HASHED_EMAIL_LIST from "../../../GraphQl/Mutations/ACTIVATE_HASHED_EMAIL_LIST";
import CREATE_HASHED_EMAIL_LIST from "../../../GraphQl/Mutations/CREATE_HASHED_EMAIL_LIST";
import UPDATE_HASHED_EMAIL_LIST from "../../../GraphQl/Mutations/UPDATE_HASHED_EMAIL_LIST";
import BULK_CREATE_HASHED_EMAIL_ADDRESS from "../../../GraphQl/Mutations/BULK_CREATE_HASHED_EMAIL_ADDRESS";
import GET_HASHED_EMAIL_LIST_WITH_COUNT from "../../../GraphQl/Queries/GET_HASHED_EMAIL_LIST_WITH_COUNT";
import GET_HASHED_EMAIL_LIST from "../../../GraphQl/Queries/GET_HASHED_EMAIL_LIST";
import DELETE_HASHED_EMAIL_LIST from "../../../GraphQl/Mutations/DELETE_HASHED_EMAIL_LIST";
import DELETE_MANY_HASHED_EMAIL_ADDRESS from "../../../GraphQl/Mutations/DELETE_MANY_HASHED_EMAILS";
import PROCESS_DEVICE_IDS from "../../../GraphQl/Mutations/PROCESS_DEVICE_IDS";
import PROCESS_MODEL_SCORE from "../../../GraphQl/Mutations/PROCESS_MODEL_SCORE";
import UPDATE_HASHED_EMAIL_LATEST_STATUS from "../../../GraphQl/Mutations/UPDATE_HASHED_EMAIL_LATEST_STATUS";
import REFRESH_AUDIENCE_CSV from "../../../GraphQl/Mutations/REFRESH_AUDIENCE_CSV";
import SET_HASHED_EMAIL_LIST_AUTO_REFRESH from "../../../GraphQl/Mutations/SET_HASHED_EMAIL_LIST_AUTO_REFRESH";
import { determineUserAccess } from "../../../permissions/userLevelAccess";

const FirstPartyDataLoader = (props) => {
  const { currentUser } = props;
  const currentOrg = currentUser.role.org;
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState(null);

  // Pagination state for hashed email lists
  const [emailListPagination, setEmailListPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  const userPermissions = determineUserAccess(currentUser.permission);
  const providersVisible = userPermissions.providersVisible;

  const [activateHashedEmailList] = useMutation(ACTIVATE_HASHED_EMAIL_LIST);

  const [createHashedEmailList] = useMutation(CREATE_HASHED_EMAIL_LIST, {
    variables: {
      orgID: currentOrg.id,
      tag: props.tag,
      uploadedAt: props.uploadedAt,
      csvFileName: props.csvFileName,
      dsp: props.dsp,
      stepProgress: props.stepProgress,
      status: props.status,
      typeOfFirstPartyData: props.typeOfFirstPartyData,
      name: props.name,
    },
  });

  const [updateHashedEmailList] = useMutation(UPDATE_HASHED_EMAIL_LIST);

  const [createHashedEmailAddress] = useMutation(BULK_CREATE_HASHED_EMAIL_ADDRESS, {
    variables: {
      orgID: currentOrg.id,
    },
  });

  // Debounce search term to avoid excessive API calls
  const debouncedSetSearchTerm = useCallback(
    debounce((term) => setDebouncedSearchTerm(term), 300),
    []
  );

  useEffect(() => {
    debouncedSetSearchTerm(searchTerm);
  }, [searchTerm, debouncedSetSearchTerm]);

  const {
    data: getHashedEmailListWithCount,
    loading: loadingHashedEmailLists,
    refetch: refetchHashedEmailLists,
  } = useQuery(GET_HASHED_EMAIL_LIST_WITH_COUNT, {
    variables: {
      orgId: selectedAdvertiserId || currentOrg.id,
      searchTerm: debouncedSearchTerm || null,
      skip: (emailListPagination.current - 1) * emailListPagination.pageSize,
      first: emailListPagination.pageSize,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  // Refetch when pagination changes
  useEffect(() => {
    refetchHashedEmailLists({
      orgId: selectedAdvertiserId || currentOrg.id,
      searchTerm: debouncedSearchTerm || null,
      skip: (emailListPagination.current - 1) * emailListPagination.pageSize,
      first: emailListPagination.pageSize,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailListPagination.current, emailListPagination.pageSize, debouncedSearchTerm, selectedAdvertiserId]);

  // Update pagination total when data changes
  useEffect(() => {
    if (getHashedEmailListWithCount && getHashedEmailListWithCount.getHashedEmailListWithCount) {
      const totalCount = getHashedEmailListWithCount.getHashedEmailListWithCount.totalCount || 0;
      setEmailListPagination((prev) => ({
        ...prev,
        total: totalCount,
      }));
    }
  }, [getHashedEmailListWithCount]);

  const { data: hashedEmailList, refetch: refetchHashedEmailList } = useQuery(
    GET_HASHED_EMAIL_LIST,
    {
      variables: { id: props.id },
      skip: true,
    }
  );

  const [deleteHashedEmailList] = useMutation(DELETE_HASHED_EMAIL_LIST);

  const [deleteManyHashedEmailAddresses] = useMutation(DELETE_MANY_HASHED_EMAIL_ADDRESS);

  const [processDeviceIds] = useMutation(PROCESS_DEVICE_IDS);

  const [processModelScore] = useMutation(PROCESS_MODEL_SCORE);

  const [updateHashedEmailLatestStatus] = useMutation(UPDATE_HASHED_EMAIL_LATEST_STATUS);

  const [refreshAudienceCsv] = useMutation(REFRESH_AUDIENCE_CSV);
  const [setHashedEmailListAutoRefresh] = useMutation(SET_HASHED_EMAIL_LIST_AUTO_REFRESH);

  return React.cloneElement(props.children, {
    ...props.children.props,
    activateHashedEmailList,
    createHashedEmailList,
    updateHashedEmailList,
    createHashedEmailAddress,
    hashedEmailLists: getHashedEmailListWithCount,
    loadingHashedEmailLists,
    refetchHashedEmailLists,
    deleteHashedEmailList,
    deleteManyHashedEmailAddresses,
    processDeviceIds,
    processModelScore,
    hashedEmailList,
    refetchHashedEmailList,
    updateHashedEmailLatestStatus,
    refreshAudienceCsv,
    setHashedEmailListAutoRefresh,
    currentOrg,
    searchTerm,
    setSearchTerm,
    selectedAdvertiserId,
    setSelectedAdvertiserId,
    providersVisible,
    emailListPagination,
    setEmailListPagination,
  });
};

export default compose(
  graphql(CURRENT_USER, {
    props: ({ data: { currentUser }, ownProps }) => {
      return ownProps.currentUser || { currentUser };
    },
  })
)(FirstPartyDataLoader);
